import { Product, SubVariant } from "@/data/products";
import { isOfferActive, calcDiscountedPrice } from "@/utils/offerUtils";

/**
 * Aggregate all sub-variants across a product's size variants into a single
 * de-duplicated list keyed by (colorHex + scent + poeticName). This lets the
 * card + filters treat scent/color as product-level attributes without caring
 * which size they hang off.
 */
export const getSubVariants = (product: Product): SubVariant[] => {
  const sizes = product.sizeVariants;
  if (!sizes || sizes.length === 0) return [];
  const seen = new Map<string, SubVariant>();
  for (const s of sizes) {
    if (!s.subVariants) continue;
    for (const sv of s.subVariants) {
      const key = [sv.colorHex ?? "", sv.scent ?? "", sv.poeticName ?? ""].join("|");
      if (key === "||") continue;
      if (!seen.has(key)) seen.set(key, sv);
    }
  }
  return Array.from(seen.values()).sort(
    (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
  );
};

/** Lowest offered/list price on the product (used for sorting + range). */
export const getMinPrice = (product: Product): number => {
  const listBase =
    product.sizeVariants && product.sizeVariants.length > 0
      ? Math.min(...product.sizeVariants.map((v) => v.price))
      : product.price;
  // account for active offers
  const offered =
    product.sizeVariants && product.sizeVariants.length > 0
      ? Math.min(
          ...product.sizeVariants.map((v) => {
            const off = isOfferActive(v.offer) ? v.offer : isOfferActive(product.offer) ? product.offer : undefined;
            return calcDiscountedPrice(v.price, off);
          })
        )
      : calcDiscountedPrice(product.price, isOfferActive(product.offer) ? product.offer : undefined);
  return Math.min(listBase, offered);
};

export const getMaxPrice = (product: Product): number =>
  product.sizeVariants && product.sizeVariants.length > 0
    ? Math.max(...product.sizeVariants.map((v) => v.price))
    : product.price;

/** Highest active discount percent across product + its variants. */
export const getBestDiscountPct = (product: Product): number => {
  const offers = [
    product.offer,
    ...(product.sizeVariants?.map((v) => v.offer) ?? []),
  ].filter((o): o is NonNullable<typeof o> => !!o && isOfferActive(o));
  if (offers.length === 0) return 0;
  const pcts = offers.map((o) =>
    o.type === "percentage" ? o.value : 0
  );
  return Math.max(0, ...pcts);
};

export const hasActiveOffer = (product: Product): boolean =>
  isOfferActive(product.offer) ||
  !!product.sizeVariants?.some((v) => isOfferActive(v.offer));

export const isInStock = (product: Product): boolean => {
  if (product.stockStatus === "out_of_stock" || product.stockQuantity === 0) return false;
  return true;
};

/** Detect a "new arrival" heuristically from badge text + featured flag. */
export const isNewArrival = (product: Product): boolean => {
  const b = `${product.badge ?? ""} ${product.badgeAr ?? ""}`.toLowerCase();
  return b.includes("new") || b.includes("جديد") || !!product.isFeatured;
};

export interface FacetOption {
  key: string;         // filter value (english scent or hex)
  label: string;       // EN label
  labelAr: string;     // AR label
  colorHex?: string;
  count: number;
}

/** Collect all scent options across the catalog with counts. */
export const collectScentFacets = (products: Product[]): FacetOption[] => {
  const map = new Map<string, FacetOption>();
  for (const p of products) {
    for (const sv of getSubVariants(p)) {
      const scent = (sv.scent ?? "").trim();
      if (!scent) continue;
      const key = scent.toLowerCase();
      const existing = map.get(key);
      if (existing) existing.count += 1;
      else
        map.set(key, {
          key,
          label: scent,
          labelAr: (sv.scentAr ?? scent).trim() || scent,
          count: 1,
        });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
};

/** Collect all color options (hex + poetic name) across the catalog. */
export const collectColorFacets = (products: Product[]): FacetOption[] => {
  const map = new Map<string, FacetOption>();
  for (const p of products) {
    for (const sv of getSubVariants(p)) {
      const hex = (sv.colorHex ?? "").trim().toLowerCase();
      if (!hex) continue;
      const existing = map.get(hex);
      if (existing) existing.count += 1;
      else
        map.set(hex, {
          key: hex,
          label: sv.poeticName ?? hex,
          labelAr: sv.poeticNameAr ?? sv.poeticName ?? hex,
          colorHex: sv.colorHex ?? undefined,
          count: 1,
        });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
};

/** Does the product expose the given scent (case-insensitive)? */
export const productHasScent = (p: Product, scentKey: string): boolean =>
  getSubVariants(p).some(
    (sv) => (sv.scent ?? "").trim().toLowerCase() === scentKey
  );

export const productHasColor = (p: Product, hexKey: string): boolean =>
  getSubVariants(p).some(
    (sv) => (sv.colorHex ?? "").trim().toLowerCase() === hexKey
  );
